import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  IconButton,
  Paper,
  Divider,
  Fade,
  Zoom,
  Skeleton,
  useTheme,
  useMediaQuery,
  Avatar
} from '@mui/material';
import {
  Search as SearchIcon,
  Event as EventIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  ArrowForward as ArrowForwardIcon,
  CalendarToday as CalendarTodayIcon,
  Inventory as InventoryIcon
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { uk } from 'date-fns/locale';
import { inventoryAPI } from '../services/api';
import AuthService from '../services/auth';
import { ArrowUpward, ArrowDownward, Save as SaveIcon, Close as CloseIcon, Sort as SortIcon } from '@mui/icons-material';

const InventoryList = () => {
  const [inventoryItems, setInventoryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date(new Date().getTime() + 24 * 60 * 60 * 1000)); // Tomorrow
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [animationComplete, setAnimationComplete] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isEditingOrder, setIsEditingOrder] = useState(false);
  const [draftItems, setDraftItems] = useState([]);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Animation effect when component mounts
  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimationComplete(true);
    }, 300);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    fetchInventoryItems();
    (async () => {
      const [auth, superAdmin] = await Promise.all([
        AuthService.isAuthenticated(),
        AuthService.isSuperAdmin()
      ]);
      setIsAuthenticated(!!auth);
      setIsSuperAdmin(!!superAdmin);
    })();
  }, []);

  const fetchInventoryItems = async () => {
    setLoading(true);
    try {
      const response = await inventoryAPI.getAllItems();
      setInventoryItems(response.data);
      setError('');
    } catch (err) {
      setError('Не вдалося завантажити елементи інвентарю. Будь ласка, спробуйте пізніше.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleBookClick = (item) => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    setSelectedItem(item);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedItem(null);
  };

  const handleProceedToBooking = () => {
    navigate('/booking/create', {
      state: {
        selectedItem,
        startDate,
        endDate
      }
    });
    handleCloseDialog();
  };

  const startEditOrder = () => {
    setDraftItems(inventoryItems.slice());
    setIsEditingOrder(true);
  };

  const cancelEditOrder = () => {
    setIsEditingOrder(false);
    setDraftItems([]);
  };

  const saveOrder = async () => {
    try {
      const ids = draftItems.map(it => it.id);
      await inventoryAPI.updateOrder(ids);
      setIsEditingOrder(false);
      setDraftItems([]);
      await fetchInventoryItems();
    } catch (e) {
      console.error('Failed to update order', e);
      setError('Не вдалося зберегти порядок. Спробуйте ще раз.');
    }
  };

  const moveItem = (index, direction) => {
    setDraftItems(prev => {
      const newArr = prev.slice();
      const newIndex = index + direction;
      if (newIndex < 0 || newIndex >= newArr.length) return prev;
      const temp = newArr[index];
      newArr[index] = newArr[newIndex];
      newArr[newIndex] = temp;
      return newArr;
    });
  };

  const baseList = isEditingOrder ? draftItems : inventoryItems;
  const filteredItems = baseList.filter(item =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Loading skeleton UI
  const renderSkeletons = () => {
    return Array(6).fill(0).map((_, index) => (
      <Grid item xs={12} sm={6} md={4} key={`skeleton-${index}`}>
        <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <CardContent>
            <Skeleton variant="rectangular" width="60%" height={28} sx={{ mb: 2 }} />
            <Skeleton variant="text" width="80%" />
            <Skeleton variant="text" width="40%" />
          </CardContent>
          <Box sx={{ flexGrow: 1 }} />
          <CardActions>
            <Skeleton variant="rectangular" width={100} height={36} sx={{ borderRadius: 1 }} />
            <Skeleton variant="rectangular" width={140} height={36} sx={{ borderRadius: 1, ml: 1 }} />
          </CardActions>
        </Card>
      </Grid>
    ));
  };

  return (
    <Container>

      {error && <Alert 
        severity="error" 
        sx={{ 
          mt: 2,
          mb: 3,
          borderRadius: 2,
          boxShadow: '0 2px 8px rgba(211, 47, 47, 0.15)'
        }}
      >
        {error}
      </Alert>}

      {isSuperAdmin && (
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          {!isEditingOrder ? (
            <Button variant="outlined" startIcon={<SortIcon />} onClick={startEditOrder}>
              Редагувати порядок
            </Button>
          ) : (
            <>
              <Button color="success" variant="contained" startIcon={<SaveIcon />} onClick={saveOrder}>
                Зберегти порядок
              </Button>
              <Button color="inherit" variant="outlined" startIcon={<CloseIcon />} onClick={cancelEditOrder}>
                Скасувати
              </Button>
            </>
          )}
        </Box>
      )}

      <Box sx={{ mb: 4 }}>
        <TextField
          fullWidth
          label="Шукати інвентар"
          variant="outlined"
          value={searchTerm}
          onChange={handleSearchChange}
          disabled={isEditingOrder}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              transition: 'box-shadow 0.3s ease',
              '&:hover': {
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
              },
              '&.Mui-focused': {
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
              }
            }
          }}
        />
      </Box>

      <Grid container spacing={3}>
        {filteredItems.length > 0 ? (
          filteredItems.map(item => (
            <Grid item xs={12} sm={6} md={4} key={item.id}>
              <Card sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                borderRadius: 2,
                overflow: 'hidden',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)'
                }
              }}>
                {item.photoUrl && (
                  <Box sx={{ 
                    width: '100%',
                    paddingTop: '100%', // This creates a square aspect ratio
                    position: 'relative',
                    overflow: 'hidden', 
                    bgcolor: 'background.paper'
                  }}>
                    <Box sx={{
                      position: 'absolute',
                      top: 16,
                      left: 16,
                      right: 16,
                      bottom: 16,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      borderRadius: '8px',
                      overflow: 'hidden'
                    }}>
                      <img 
                        src={item.photoUrl} 
                        alt={item.name} 
                        style={{ 
                          width: '100%', 
                          height: '100%', 
                          objectFit: 'contain' 
                        }} 
                      />
                    </Box>
                  </Box>
                )}
                {!item.photoUrl && (
                  <Box sx={{ 
                    width: '100%',
                    paddingTop: '100%', // This creates a square aspect ratio
                    position: 'relative',
                    bgcolor: 'grey.100'
                  }}>
                    <Box sx={{
                      position: 'absolute',
                      top: 16,
                      left: 16,
                      right: 16,
                      bottom: 16,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      borderRadius: '8px',
                      overflow: 'hidden'
                    }}>
                      <InventoryIcon sx={{ fontSize: 80, color: 'grey.400' }} />
                    </Box>
                  </Box>
                )}
                <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                  <Typography 
                    variant="h6" 
                    component="div" 
                    sx={{ 
                      fontWeight: 600,
                      mb: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      lineHeight: 1.3
                    }}
                  >
                    {item.name}
                  </Typography>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    mt: 2,
                    mb: 1
                  }}>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ fontWeight: 500 }}
                    >
                      Кількість: {item.totalQuantity}
                    </Typography>
                  </Box>
                </CardContent>
                <Divider />
                <CardActions sx={{ p: 1.5, justifyContent: 'space-between' }}>
                  {isEditingOrder ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', gap: 1 }}>
                      {(() => {
                        const idx = draftItems.findIndex(d => d.id === item.id);
                        const isFirst = idx === 0;
                        const isLast = idx === draftItems.length - 1;
                        return (
                          <>
                            <IconButton aria-label="move up" onClick={() => moveItem(idx, -1)} disabled={isFirst}>
                              <ArrowUpward />
                            </IconButton>
                            <IconButton aria-label="move down" onClick={() => moveItem(idx, 1)} disabled={isLast}>
                              <ArrowDownward />
                            </IconButton>
                          </>
                        );
                      })()}
                    </Box>
                  ) : (
                    <>
                      <Button 
                        variant="contained"
                        size="small" 
                        color="primary"
                        onClick={() => handleBookClick(item)}
                        startIcon={<EventIcon />}
                        sx={{
                          borderRadius: 1.5,
                          fontWeight: 500,
                          textTransform: 'none',
                          boxShadow: 'none',
                          height: '48px',
                          width: isMobile ? '48%' : isTablet ? '120px' : 'auto',
                          minWidth: '120px',
                          whiteSpace: 'nowrap',
                          px: isTablet ? 1.5 : 2,
                          '&:hover': {
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                          }
                        }}
                      >
                        Забронювати
                      </Button>
                      <Button 
                        size="small"
                        variant="outlined"
                        onClick={() => navigate(`/inventory/${item.id}/availability`)}
                        endIcon={<ArrowForwardIcon />}
                        sx={{
                          borderRadius: 1.5,
                          textTransform: 'none',
                          fontWeight: 500,
                          height: '48px',
                          width: isMobile ? '48%' : 'auto',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          px: 2
                        }}
                      >
                        {isMobile ? 'Наявність' : 'Перевірити наявність'}
                      </Button>
                    </>
                  )}
                </CardActions>
              </Card>
            </Grid>
          ))
        ) : (
          <Grid item xs={12}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 4, 
                textAlign: 'center',
                borderRadius: 2,
                bgcolor: 'background.paper',
                border: '1px dashed',
                borderColor: 'divider'
              }}
            >
              <SearchIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2, opacity: 0.6 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Елементи інвентарю не знайдено
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 500, mx: 'auto', mb: 2 }}>
                Спробуйте змінити пошуковий запит або перевірте наявність інвентарю пізніше.
              </Typography>
              {searchTerm && (
                <Button 
                  variant="outlined" 
                  onClick={() => setSearchTerm('')}
                  sx={{ mt: 1, borderRadius: 2 }}
                >
                  Очистити пошук
                </Button>
              )}
            </Paper>
          </Grid>
        )}
      </Grid>

      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden'
          }
        }}
      >
        <Box sx={{ 
          bgcolor: 'primary.main', 
          color: 'white',
          py: 2,
          px: 3
        }}>
          <DialogTitle sx={{ p: 0, fontWeight: 'bold' }}>
            Бронювання: {selectedItem?.name}
          </DialogTitle>
        </Box>
        <DialogContent sx={{ p: 3, mt: 1 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Будь ласка, оберіть дату та час для бронювання:
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={uk}>
            <Box sx={{ mt: 3 }}>
              <DateTimePicker
                label="Дата і час отримання"
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                format="dd.MM.yyyy HH:mm"
                sx={{ 
                  width: '100%', 
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2
                  }
                }}
              />
              <DateTimePicker
                label="Дата і час повернення"
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                format="dd.MM.yyyy HH:mm"
                sx={{ 
                  width: '100%',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2
                  }
                }}
              />
            </Box>
          </LocalizationProvider>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={handleCloseDialog}
            variant="outlined"
            sx={{ 
              borderRadius: 2,
              px: 3,
              '&:hover': {
                transform: 'translateY(-2px)',
                transition: 'all 0.3s ease'
              }
            }}
          >
            Скасувати
          </Button>
          <Button 
            onClick={handleProceedToBooking} 
            variant="contained"
            sx={{ 
              borderRadius: 2,
              px: 3,
              fontWeight: 'bold',
              boxShadow: '0 4px 12px rgba(218, 6, 6, 0.2)',
              '&:hover': {
                boxShadow: '0 6px 16px rgba(218, 6, 6, 0.3)',
                transform: 'translateY(-2px)',
                transition: 'all 0.3s ease'
              }
            }}
          >
            Продовжити
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default InventoryList;
